import { FileProps } from "../..";
import { Log } from "../generic";
import { MahsResume } from "../mahTypes";
import { SceStatus } from "../sceTypes";
import { TagStatusType } from "../tagTypes";
import { User } from "../userTypes";
import { WoState } from "../woTypes";
import { CreationTypeEnum, SceConditionEnum } from "./ctoTypes";
import { BarrierTriage, BarrierTriageStatus } from "./barrierTriageTypes";

export enum MergeStatus {
  MERGED = "MERGED",
  DECLINED = "DECLINED",
  PENDING = "PENDING",
}

export enum RolesAssessmentTeamEnum {
  RISK_ASSESSMENT_FACILITY = 1,
  HSE_REPRESENTATIVE = 2,
  RELEVANT_SUPERINTENDENT = 3,
  OPERATION_ENGINEER = 4,
  OTHER = 5,
}

export enum AccountabilityStatus {
  AWAITING_ACCOUNTABILITY_CONFIRMATION = "AWAITING_ACCOUNTABILITY_CONFIRMATION",
  CONFIRMED = "CONFIRMED",
  REASSESS_ACCOUNTABILITY = "REASSESS_ACCOUNTABILITY",
}

export interface AccountabilityConfirmationRequest {
  agreement: boolean;
  comment?: string | null;
}

export type OIMAccountabilityConfirmationRequest =
  AccountabilityConfirmationRequest;

export interface AccountabilityConfirmationResponse {
  user: User;
  position?: string | null;
  confirmed_at: string;
  comment: string | null;
}

export type OIMAccountabilityConfirmationResponse =
  AccountabilityConfirmationResponse;

export interface AssessmentTeamCreate {
  role_code: RolesAssessmentTeamEnum;
  user_id: number;
}

export interface AssessmentTeamResponse {
  role_code: RolesAssessmentTeamEnum;
  user: User;
}

export interface Step1Props {
  sce_condition_code: SceConditionEnum | null;
  sce_condition_comment: string | null;
  possible_to_manage: boolean | null | string;
  possible_to_manage_comment: string | null;
  possible_to_manage_files: FileProps[];
  safe_to_operate_short_duration: boolean | null;
  justification: string | null;
  justification_files: FileProps[] | undefined;
  initiation_files?: FileProps[] | null;
  draft: boolean;
}

export interface CreateStep1Body extends Step1Props {
  assessment_team: AssessmentTeamCreate[];
}

export interface CreateStep1Form extends CreateStep1Body {
  assessment_team_plus: (string | number)[];
}

export interface Step1Response extends Step1Props, Log {
  cto_code: string;
  creation_type: CreationTypeEnum;
  tags: TagStatusType[];
  sces: SceStatus[];
  wos: WoState[];
  related_ctos: string[];
  affected_mahs: MahsResume[];
  assessment_team: AssessmentTeamResponse[];
  can_user_fill_step: boolean;
  eligible_users?: string[];
  related_mocs?: Moc[];
  cto_summary: string;
  step_approved_by: User | null;
  step_approved_at: Date | null;
}

export interface Moc {
  moc_code: string;
  moc_status: string;
}

interface NewStep1Props {
  sce_condition_code: SceConditionEnum | null;
  sce_condition_comment: string | null;
  possible_to_manage: boolean | null | string;
  possible_to_manage_comment: string | null;
  mopo_reviewed?: boolean;
  possible_to_manage_files: FileProps[];
  safe_to_operate_short_duration: boolean | null;
  mitigation_implemented?: string | null;
  mitigation_implementation_comments?: string | null;
  justification: string | null;
  justification_files: FileProps[] | undefined;
  initiation_files?: FileProps[] | null;
  draft: boolean;
  cto_title?: string;
}

export interface CreateNewStep1Body extends NewStep1Props {
  assessment_team: AssessmentTeamCreate[];
  barrier_triage?: {
    is_barrier_affected: boolean;
    barrier_affected_justification?: string;
  };
}

export interface CreateNewStep1Form extends CreateNewStep1Body {
  assessment_team_plus: (string | number)[];
}

export interface PostNewStep1SecondRespondentBody {
  barrier_triage: {
    is_barrier_affected: boolean;
    barrier_affected_justification: string | null;
  };
  draft?: boolean;
}

export type PostNewStep1Body =
  | CreateNewStep1Body
  | PostNewStep1SecondRespondentBody;

export interface NewStep1Response extends NewStep1Props {
  cto_code: string;
  creation_type: CreationTypeEnum;
  tags: TagStatusType[];
  sces: SceStatus[];
  wos: WoState[];
  related_ctos: string[];
  affected_mahs: MahsResume[];
  assessment_team: AssessmentTeamResponse[];
  can_user_fill_step: boolean;
  eligible_users?: string[];
  related_mocs?: Moc[];
  cto_summary: string;
  step_approved_by: User | null;
  step_approved_at: Date | null;
  merge_status: MergeStatus | null;
  barrier_triage: BarrierTriage | null;
  barrier_triage_status?: BarrierTriageStatus | null;
  accountability_status?: AccountabilityStatus | null;
  current_step_action?: string | null;
  accountability_confirmations?: AccountabilityConfirmationResponse[];
}
